import { useState, type FormEvent } from "react";
import { Link, useNavigate, useSearchParams } from "react-router";
import {
  Button,
  Callout,
  FormGroup,
  InputGroup,
  Intent,
} from "@blueprintjs/core";

import { confirmPasswordReset, isValidWorkEmail, prettifyAuthError } from "../lib/auth";
import { SystemUseNotification } from "../components/system-use-notification";

function policyIssues(pw: string): string[] {
  const issues: string[] = [];
  if (pw.length < 12) issues.push("at least 12 characters");
  if (!/[A-Z]/.test(pw)) issues.push("an uppercase letter");
  if (!/[a-z]/.test(pw)) issues.push("a lowercase letter");
  if (!/[0-9]/.test(pw)) issues.push("a number");
  if (!/[^A-Za-z0-9]/.test(pw)) issues.push("a symbol");
  return issues;
}

export function ResetPassword() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [email, setEmail] = useState(params.get("email") ?? "");
  const [code, setCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const emailTouched = email.length > 0;
  const emailValid = isValidWorkEmail(email);
  const emailHelper =
    emailTouched && !emailValid
      ? "Enter a valid email address."
      : undefined;
  const policyFailures = newPassword ? policyIssues(newPassword) : [];
  const passwordsMatch = newPassword === confirmPassword;
  const canSubmit =
    emailValid &&
    code.trim().length > 0 &&
    newPassword.length > 0 &&
    policyFailures.length === 0 &&
    passwordsMatch;

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await confirmPasswordReset(email, code.trim(), newPassword);
      // CJIS: one-shot code — clear from memory immediately.
      setCode("");
      setNewPassword("");
      setConfirmPassword("");
      navigate("/login", {
        replace: true,
        state: { resetSuccess: true },
      });
    } catch (err) {
      // Clear the code on failure too — codes are one-shot from Cognito's perspective.
      setCode("");
      setError(prettifyAuthError((err as Error).message));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="reset-password" className="ic-auth-surface" style={shellStyle}>
      <div className="ic-auth-card" style={cardStyle}>
        <div style={brandStyle}>
          <span className="ic-auth-logo" role="img" aria-label="IronCase" />
          <div style={{ fontSize: 12, color: "var(--bp-text-muted)" }}>
            Set new password
          </div>
        </div>

        <form onSubmit={onSubmit} style={{ marginTop: 24 }}>
          <FormGroup
            label="Email"
            labelFor="email"
            intent={emailHelper ? Intent.DANGER : Intent.NONE}
            helperText={emailHelper}
          >
            <InputGroup
              id="email"
              data-testid="reset-password-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              leftIcon="envelope"
              required
              autoComplete="email"
              intent={emailHelper ? Intent.DANGER : Intent.NONE}
            />
          </FormGroup>

          <FormGroup
            label="Verification code"
            labelFor="code"
            helperText="Check your email for a 6-digit code."
          >
            <InputGroup
              id="code"
              data-testid="reset-password-code"
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              leftIcon="key"
              required
              autoFocus
            />
          </FormGroup>

          <FormGroup label="New password" labelFor="new-password">
            <InputGroup
              id="new-password"
              data-testid="reset-password-new-password"
              type={showPw ? "text" : "password"}
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              leftIcon="lock"
              required
              autoComplete="new-password"
              rightElement={
                <Button
                  data-testid="reset-password-toggle"
                  minimal
                  small
                  icon={showPw ? "eye-off" : "eye-open"}
                  onClick={() => setShowPw((v) => !v)}
                  aria-label={showPw ? "Hide password" : "Show password"}
                />
              }
            />
          </FormGroup>
          {newPassword && policyFailures.length > 0 && (
            <div
              style={{
                fontSize: 11,
                color: "var(--bp-text-dim)",
                marginTop: -8,
                marginBottom: 12,
              }}
            >
              Needs {policyFailures.join(", ")}.
            </div>
          )}
          {!newPassword && (
            <div
              style={{
                fontSize: 11,
                color: "var(--bp-text-dim)",
                marginTop: -8,
                marginBottom: 12,
              }}
            >
              At least 12 characters, with upper, lower, number, and symbol.
            </div>
          )}

          <FormGroup
            label="Confirm new password"
            labelFor="confirm-password"
            intent={confirmPassword && !passwordsMatch ? Intent.DANGER : Intent.NONE}
            helperText={
              confirmPassword && !passwordsMatch ? "Passwords don't match." : undefined
            }
          >
            <InputGroup
              id="confirm-password"
              data-testid="reset-password-confirm-password"
              type={showPw ? "text" : "password"}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              leftIcon="lock"
              required
              autoComplete="new-password"
              intent={confirmPassword && !passwordsMatch ? Intent.DANGER : Intent.NONE}
            />
          </FormGroup>

          {error && (
            <Callout intent={Intent.DANGER} icon="error" style={{ marginBottom: 12 }}>
              {error}
            </Callout>
          )}

          <Button
            type="submit"
            data-testid="reset-password-submit"
            intent={Intent.PRIMARY}
            text="Set new password"
            rightIcon="tick"
            fill
            large
            loading={submitting}
            disabled={!canSubmit}
          />
        </form>

        <div style={{ marginTop: 12, textAlign: "center" }}>
          <Link
            to="/login"
            data-testid="reset-password-back-to-login"
            style={{
              fontSize: 12,
              color: "var(--bp-text-muted)",
              textDecoration: "none",
            }}
          >
            Back to sign in
          </Link>
        </div>

        {/* CJIS §5.5.2 requires the system-use notification on every surface
            where a person interacts with the system before being granted
            access — not only the sign-in form. This page carried the condensed
            footer alone, so someone arriving from a password email never saw
            the full notice. Passive banner (no acknowledgement): there is no
            credential submission here to gate on it. */}
        <SystemUseNotification variant="banner" />
        <SystemUseNotification variant="footer" />
      </div>
    </div>
  );
}

const shellStyle: React.CSSProperties = {
  minHeight: "100vh",
  display: "grid",
  placeItems: "center",
  background:
    "radial-gradient(circle at 30% 20%, var(--bp-bg-elev-1) 0%, var(--bp-bg) 60%)",
  padding: 24,
};

const cardStyle: React.CSSProperties = {
  width: "100%",
  maxWidth: 460,
  background: "var(--bp-bg-elev-1)",
  border: "1px solid var(--bp-border)",
  borderRadius: 8,
  padding: 28,
  // Shadow lives in _login.scss (.ic-auth-card) so it can differ per theme —
  // an inline value would win over the stylesheet and defeat that rule.
};

const brandStyle: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: 4,
  paddingBottom: 8,
  borderBottom: "1px solid var(--bp-border-soft)",
};

